import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../../store';
import axios from '../../../axios';
import { ToastAnt } from '../Toast/ToastAnt';

export type SearchFields = {
  site: string | undefined;
  inspection: string | undefined;
  turbine: string | undefined;
  status: string | undefined;
  findingType: string | undefined;
  severity: string | undefined;
  layer: string | undefined;
  findingID: string | undefined;
  assessor: string | undefined;
};

type LandingQuickFiltersRequest = {
  site: string[];
  turbine: string[];
  inspection: string[];
  status: number[];
  severity: string[];
  findingID: string[];
};

type LandingQuickFiltersResponse = {
  site: string[];
  turbine: string[];
  inspection: string[];
  newStatus: string[];
  severity: string[];
};

type PaggingConfiguration = {
  take: number;
  skip: number;
  getTotal: boolean;
  filters: LandingQuickFiltersRequest;
};

type PaggingResult = {
  totalNumber?: number;
  elements: TurbineInfo[];
  filters: LandingQuickFiltersResponse;
};

export enum TurbineStatusText {
  'Waiting for upload' = 0,
  'Waiting for reupload' = 50,
  'Upload in progress' = 100,
  'Upload completed' = 200,
  'Validation in progress' = 250,
  'Validation completed' = 300,
  'Waiting for data transfer' = 330,
  'Data transfered' = 360,
  'Waiting for QA assessment' = 400,
  'QA assessment in progress' = 430,
  'QA assessment completed' = 460,
  'Stitching in progress' = 600,
  'ML Detection in progress' = 1100,
  'Waiting for review' = 1300,
  'Review in progress' = 1400,
  'Review on hold' = 1401,
  'Review completed' = 1500,
  'Waiting for QA review' = 1900,
  'QA review in progress' = 2000,
  'QA review on hold' = 2001,
  'QA review completed' = 2100,
  'Published' = 2200,
  'Closed' = 2400
}

export enum TurbineStatus {
  WaitingForUpload = 0,
  WaitingForReupload = 50,
  UploadInProgress = 100,
  UploadCompleted = 200,
  ValidationInProgress = 250,
  ValidationCompleted = 300,
  WaitingForDataTransfer = 330,
  DataTransfered = 360,
  WaitingForQAAssessment = 400,
  QAAssessmentInProgress = 430,
  QAAssessmentCompleted = 460,
  StitchingInProgress = 600,
  MlDetectionInProgress = 1100,
  WaitingForReview = 1300,
  ReviewInProgress = 1400,
  ReviewOnHold = 1401,
  ReviewCompleted = 1500,
  WaitingForQaReview = 1900,
  QaReviewInProgress = 2000,
  QaReviewOnHold = 2001,
  QaReviewCompleted = 2100,
  Published = 2200,
  Closed = 2400
}

export type TurbineInfo = {
  site: string;
  turbine: string;
  inspection: string;
  status: TurbineStatus;
  canOpenStitchingTool: boolean;
  canOpenAnnotationManager: boolean;
  sequences: SequenceInfo[];
  userName: string;
  userId: string;
  turbineId: string;
  standardId: string;
  isArchived: boolean;
};

export type SequenceInfo = {
  blade: string;
  bladeSide: string;
  status: string;
};

export type Assignee = {
  id: string;
  fullName: string;
};

export type BulkTurbinesUpdateConfig = {
  assigneeId?: string;
  assigneeFullName?: string;
  status?: string;
  turbinePaths: string[];
};

type SelectedValues = {
  selectedSites: string[];
  selectedTurbines: string[];
  selectedInspections: string[];
  selectedStatuses: string[];
  selectedSeverities: string[];
  selectedFindingIDs: string[];
  [key: string]: string[];
};

export const take = 20;

type State = {
  sites: string[];
  inspections: string[];
  turbines: string[];
  statuses: string[];
  severities: string[];
  turbineInfos: TurbineInfo[];
  currentPage: number;
  size: number;
  totalNumberOfTurbines: number;
  selectedValues: SelectedValues;
  isLoading: boolean;
  assignees: Assignee[];
  isPublishing: boolean;
  showBulkUpdateModal: boolean;
  clearSelection: boolean;
  publishError: string | null;
  allHiddenColumns: string[];
  bulkUpdateIssues: string[];
};

const initialState: State = {
  sites: [],
  inspections: [],
  turbines: [],
  turbineInfos: [],
  statuses: [],
  severities: [],
  currentPage: 0,
  size: take,
  totalNumberOfTurbines: 0,
  isLoading: true,
  selectedValues: {
    selectedSites: [],
    selectedTurbines: [],
    selectedInspections: [],
    selectedStatuses: [],
    selectedSeverities: [],
    selectedFindingIDs: []
  },
  assignees: [],
  isPublishing: false,
  showBulkUpdateModal: false,
  clearSelection: false,
  publishError: null,
  allHiddenColumns: [],
  bulkUpdateIssues: []
};

const annotationSlice = createSlice({
  name: 'landing',
  initialState,
  reducers: {
    setTurbineInfos(state, action: PayloadAction<{ turbines: TurbineInfo[]; totalNumber?: number; currentPage: number }>) {
      const { turbines, currentPage, totalNumber } = action.payload;
      state.turbineInfos = turbines;
      state.currentPage = currentPage;
      if (totalNumber) {
        state.totalNumberOfTurbines = totalNumber;
      }
    },
    setSequences(state, action: PayloadAction<{ site: string; inspection: string; turbine: string; sequences: SequenceInfo[] }>) {
      const { site, inspection, turbine, sequences } = action.payload;
      state.turbineInfos = state.turbineInfos.map(x => {
        if (x.site === site && x.inspection === inspection && x.turbine === turbine) {
          return {
            ...x,
            sequences
          };
        }

        return x;
      });
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setSearchTerms(state, action: PayloadAction<SearchFields>) {
      const { inspection, turbine, site, status, severity, findingID } = action.payload;

      state.selectedValues.selectedSites = site ? [site] : [];
      state.selectedValues.selectedInspections = inspection ? [inspection] : [];
      state.selectedValues.selectedTurbines = turbine ? [turbine] : [];
      state.selectedValues.selectedStatuses = status ? [status] : [];
      state.selectedValues.selectedSeverities = severity ? [severity] : [];
      state.selectedValues.selectedFindingIDs = findingID ? [findingID] : [];
    },
    setSearchOptions: (state, action: PayloadAction<LandingQuickFiltersResponse>) => {
      const { site, inspection, turbine, newStatus, severity } = action.payload;

      state.sites = site;
      state.inspections = inspection;
      state.turbines = turbine;
      state.statuses = newStatus;
      state.severities = severity;
    },
    setSize: (state, action: PayloadAction<number>) => {
      state.size = action.payload;
    },
    setAssignees: (state, action: PayloadAction<Assignee[]>) => {
      state.assignees = action.payload;
    },
    setIsPublishing: (state, action: PayloadAction<boolean>) => {
      state.isPublishing = action.payload;
    },
    setShowBulkUpdateModal: (state, action: PayloadAction<boolean>) => {
      state.showBulkUpdateModal = action.payload;
    },
    setClearSelection: (state, action: PayloadAction<boolean>) => {
      state.clearSelection = action.payload;
    },
    setPublishError: (state, action: PayloadAction<string | null>) => {
      state.publishError = action.payload;
    },
    setAllHiddenColumns(state, action: PayloadAction<string[]>) {
      state.allHiddenColumns = action.payload;
    },
    setBulkUpdateIssues(state, action: PayloadAction<string[]>) {
      state.bulkUpdateIssues = action.payload;
    }
  }
});

export const {
  setTurbineInfos,
  setSequences,
  setLoading,
  setSearchTerms,
  setSearchOptions,
  setSize,
  setAssignees,
  setIsPublishing,
  setShowBulkUpdateModal,
  setClearSelection,
  setPublishError,
  setAllHiddenColumns,
  setBulkUpdateIssues
} = annotationSlice.actions;

export default annotationSlice.reducer;

export const getTurbinesInfo =
  (init: boolean, take: number, newPage: number): AppThunk =>
  async (dispatch, getState) => {
    const { selectedSites, selectedTurbines, selectedInspections, selectedStatuses, selectedSeverities, selectedFindingIDs } =
      getState().landing.selectedValues;

    const size = getState().landing.size;

    if (take !== size) {
      dispatch(setSize(take));
    }

    const statusesNumerical = selectedStatuses.map(status => TurbineStatusText[status as keyof typeof TurbineStatusText]);

    const config: PaggingConfiguration = {
      take: take,
      skip: (newPage - 1) * take,
      getTotal: init,
      filters: {
        site: selectedSites,
        inspection: selectedInspections,
        turbine: selectedTurbines,
        status: statusesNumerical,
        severity: selectedSeverities,
        findingID: selectedFindingIDs
      }
    };

    dispatch(setLoading(true));

    try {
      const response = await axios.post<PaggingResult>(`home/turbinesinfo`, config);

      if (response && response.data) {
        const { elements, totalNumber, filters } = response.data;

        dispatch(
          setTurbineInfos({
            currentPage: newPage,
            turbines: elements,
            totalNumber: totalNumber
          })
        );
        dispatch(setSearchOptions(filters));
      }
    } catch (error) {}
    dispatch(setLoading(false));
  };

export const getSequencesInfo =
  (site: string, inspection: string, turbine: string): AppThunk =>
  async dispatch => {
    try {
      const response = await axios.get<SequenceInfo[]>(`home/sequencesinfo/${site}/${inspection}/${turbine}`);

      if (response && response.data) {
        dispatch(
          setSequences({
            site,
            inspection,
            turbine,
            sequences: response.data
          })
        );
      }
    } catch (error) {}
  };

export const searchTurbines =
  (fields: SearchFields): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().landing;

    dispatch(setSearchTerms(fields));
    dispatch(getTurbinesInfo(true, size, 1));
  };

export const getAssignees = (): AppThunk => async (dispatch, getState) => {
  try {
    const response = await axios.get<Assignee[]>('home/getAssingees');
    const assignees = response.data;
    dispatch(setAssignees(assignees));
  } catch (error) {}
};

export const changeAssignee =
  (id: string | undefined, fullName: string | undefined, turbineId: string, isRemoving: boolean): AppThunk =>
  async (dispatch, getState) => {
    try {
      const { size } = getState().landing;

      const updateAssigneeModel = {
        userId: id,
        fullName: fullName,
        turbineId: turbineId,
        isRemoving: isRemoving
      };

      const response = await axios.post<boolean>('home/changeAssignee', updateAssigneeModel);

      if (response) {
        dispatch(getTurbinesInfo(true, size, 1));

        isRemoving ? ToastAnt('Assignee is removed') : ToastAnt('Assignee is changed');
      }
    } catch (error) {}
  };

export const bulkTurbineUpdate =
  (model: BulkTurbinesUpdateConfig): AppThunk =>
  async (dispatch, getState) => {
    try {
      const { size } = getState().landing;

      const response = await axios.post<string[]>('turbine/bulkTurbinesUpdate', model);

      dispatch(setBulkUpdateIssues(response.data));

      if (response.data && response.data.length) {
        ToastAnt('There have been issues with updating turbines.');
      } else {
        ToastAnt('All turbines updated.');
      }

      dispatch(getTurbinesInfo(true, size, 1));
    } catch (error) {
      ToastAnt('An error occured.');
    }
  };

export const bulkTurbinePublish =
  (turbineIds: string[], resolution: string): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().landing;

    try {
      dispatch(setIsPublishing(true));

      const response = await axios.post<boolean>('turbine/bulkPublish', { turbineIds, resolution });

      ToastAnt('All turbines are published successfully');

      dispatch(setShowBulkUpdateModal(false));
      dispatch(setClearSelection(true));
    } catch (error) {
      dispatch(setPublishError((error as any).response.data));
    }

    dispatch(getTurbinesInfo(true, size, 1));
    dispatch(setIsPublishing(false));
  };

export type PublishRequest = {
  selectedTurbinePaths: string[];
  findingTypes: string[];
  findingIds: string[];
  sequences: string[];
  filters: FilterRowSelection[];
  reviewsCompleted: boolean;
  assessmentsCompleted: boolean;
};

export type FilterRowSelection = {
  selectedParameter?: string;
  selectedOperation?: string;
  selectedValue?: string;
  selectedConjuction?: string;
};

export const publishTurbines =
  (request: PublishRequest): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().landing;

    try {
      dispatch(setIsPublishing(true));

      const response = await axios.post<boolean>('turbine/conditionalPublish', request);
    } catch (error) {
      dispatch(setPublishError((error as any).response.data));
    }

    dispatch(getTurbinesInfo(true, size, 1));
    dispatch(setIsPublishing(false));
  };
